import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';

import { ITmLandingProps } from '@/components/templates';
import { IImageCMSProps } from '../utils/parseHelpers';
import { IMiniCtaBlockBCSProps } from '@/components/organisms/or-mini-cta-block-b';
import { IMiniCtaBlockCCSProps } from '@/components/organisms/or-mini-cta-block-c/or-mini-cta-block-c.types';
import { IMiniCtaBlockACSProps } from '@/components/organisms/or-mini-cta-block-a/or-mini-cta-block-a.types';
import { IOrLeadGenFormCmsProps } from '@/components/organisms/or-lead-gen-form/or-lead-gen-form.types';
import { IOrCardsListCMSProps } from '@/components/organisms/or-cards-list/or-card-list.types';
import { EOrContentMediaBlockSize } from '@/components/organisms/or-content-media-block/or-content-media-block.types';
import { IOrDesignStudioBlockCMSProps } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';
import { IOrDisclaimerTextCMSProps } from '@/components/organisms/or-disclaimer-text/or-disclaimer-text.types';
import { IOrFloorPlanViewerCMSProps } from '@/components/organisms/or-floorplan-viewer/or-floorplan-viewer.types';
import { IOrHomePlansCarouselCMSProps } from '@/components/organisms/or-home-plans-carousel/or-home-plans-carousel.types';
import { IOrImageGalleryCMSProps } from '@/components/organisms/or-image-gallery/or-image-gallery.types';
import { IOrModelHomeGridCsProps } from '@/components/organisms/or-model-home-grid/or-model-home-grid.types';
import { IOrStatsListCMSProps } from '@/components/organisms/or-stats-list/or-stats-list.types';
import { IOrThreeBlocksPropsCMS } from '@/components/organisms/or-three-blocks/or-three-blocks.types';
import { ITmDesignCenterDetailsCSProps } from '@/components/templates/tm-design-center-detail/tm-design-center-details.types';
import { ITmHomePlanTemplateCSProps } from '@/components/templates/tm-home-plan-detail/tm-home-plan-detail.types';
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';
import { EImagePosition } from '@/components/atoms/at-media/at-media.types';
import { IMlCopySectionCSProps } from '@/components/molecules/ml-copy-section/ml-copy-section.types';
import {
  EMlHeaderSize,
  IMlHeaderCMSProps,
} from '@/components/molecules/ml-header/ml-header.types';
import { ITmModelHomeDetailCSProps } from '@/components/templates/tm-model-home-detail/tm-model-home-detail.types';
import { IOrGenericContentCMSProps } from '@/components/organisms/or-generic-content/or-generic-content.types';

export enum EBlockAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export type TComponent =
  | IOrContentBlockCSProps
  | IMlAccordionBlockCSProps
  | IOrFaqListCSProps
  | IOrContentMediaBlockCSProps
  | IOrStatsListCMSProps
  | IOrCardsListCMSProps
  | TOrTestimonialBlockCSProps
  | IOrHomePlansCarouselCMSProps
  | TOrFeaturedVideoCSProps
  | TOrTestimonialBlockCSProps
  | TOrPromotionalBlockCSProps
  | IOrVideoHeroCSProps
  | TOrExteriorSwitcherCSProps
  | IOrThreeBlocksPropsCMS
  | IOrDesignStudioBlockCMSProps
  | IOrFloorPlanViewerCMSProps
  | ITmHomePlanTemplateCSProps
  | ITmLandingProps
  | IMiniCtaBlockACSProps
  | ITmDesignCenterDetailsCSProps
  | ITmModelHomeDetailCSProps
  | IOrFilteredGridProps
  | IOrLeadGenFormCmsProps
  | IMlCopySectionCSProps
  | IOrModelHomeGridCsProps
  | IMiniCtaBlockBCSProps
  | IMiniCtaBlockCCSProps
  | IOrMappingToolCSProps
  | IOrModelHomeGridCsProps
  | IMlHeaderCMSProps
  | IMlCtaCardBlockCS
  | IOrImageGalleryCMSProps['block']
  | IMiniCtaBlockDCMSProps
  | IMlServiceLocationsCS
  | IOrDisclaimerTextCMSProps
  | ISeriesModelHomeGridCMSProps
  | IOrGenericContentCMSProps;

export interface IMiniCtaBlockDCMSProps extends IDefaultCsProps {
  /** Internal Contentstack content type UID */
  _content_type_uid: 'mini_cta_block_d_2_2_7'; //mini-cta-block-d
  theme: { theme: keyof typeof EThemes };
  heading: string;
  heading_element: { select: THeaderSupportedElements };
  heading_variant: { select: THeaderSupportedElements };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  lead_gen_form?: IOrLeadGenFormCmsProps[];
  body_copy: string;
  cta_button_bottom: IAtLinkGlobalFieldProps;
  cta_button_top: IAtLinkGlobalFieldProps;
  cta_image?: IImageGlobalCSProps;
}

export type TRenderProps = {
  contentTypeUid?: string;
  showAbaForm?: boolean;
  entryUid?: string;
  locale?: string;
  body: TComponent[];
  isPage?: boolean;
};

export type TMetaData = { uid: string };

export type TIconCard = {
  icon: keyof typeof EIcons;
  title: string;
  description: string;
  _metadata: TMetaData;
  headingElement?: {
    select: keyof typeof EMlHeaderSize;
  };
};

type TRightContent = IIconCardItem | IListItem;

export interface IIconCardItem {
  icon_card: {
    description: string;
    title: string;
    icon: EIcons;
    title_element?: { select: THeaderSupportedElements };
    _metadata: TMetaData;
  };
}

export interface IListItem {
  list: {
    content: string;
    _metadata: TMetaData;
  };
}

export enum EThemes {
  LIGHT = 'light',
  DARK = 'dark',
}

export interface IDefaultCsProps {
  created_at?: string;
  created_by?: string;
  locale?: string;
  updated_by?: string;
  updated_at?: string;
  uid?: string;
  ACL?: {};
}

export interface IPromo extends IDefaultCsProps {
  chip_copy: string;
  cta: IAtLinkGlobalFieldProps;
  design_studios: {
    location: string;
  };
  promo_image: IImageProps;
  image: IImageGlobalCSProps;
  promo_description: string;
  title: string;
}

export interface IAtIconCsProps extends IDefaultCsProps {
  select?: any;
  name: {
    icon: keyof EIcons;
  };
  position: {
    select?: 'left' | 'right';
  };
  size?: keyof EClickableSize;
  title: string;
}

export interface IAtLinkCsProps extends IDefaultCsProps {
  icon: IAtIconCsProps[];
  outline: boolean;
  size: EClickableSize;
  target: EClickableTarget;
  title: string;
  type: EClickableTypes;
  underline: string;
  aria_label: string;
  url: string;
  variant: {
    variant: keyof typeof EClickableVariants;
  };
  _content_type_uid: 'link';
}

export interface IFileCsProps extends IDefaultCsProps {
  filename: string;
  url: string;
  file_size: string;
  is_dir: boolean;
  content_type: string;
  parent_uid: string | null;
  tags?: string[];
  dimension?: {
    width: number;
    height: number;
  };
  // description comes from asset file, however we cannot require it
  // used for alt text on image assets
  description?: string;
}

export interface IAtLinkGlobalFieldProps {
  icon?: IAtIconCsProps[];
  label: string;
  outline: boolean;
  size: keyof typeof EClickableSize;
  target: EClickableTarget | keyof typeof EClickableTarget;
  type: EClickableTypes;
  aria_label?: string;
  title?: string;
  url: {
    title: string;
    href: string;
  };
  variant: keyof typeof EClickableVariants;
}

export interface IGlobalThemeField {
  theme: keyof typeof EThemes;
}

export interface IMlcopyBlockCSProps extends IDefaultCsProps {
  block_header?: string;
  title: string;
  description: string;
  eyebrow?: string;
  subhead?: string;
  theme: {
    theme: EThemes;
  };
  alignment: {
    alignmentprop: keyof typeof EBlockAlignment;
  };
  heading_element?: {
    select?: THeaderSupportedElements;
  };
  heading_variant?: {
    select?: THeaderSupportedElements;
  };
  link: IAtLinkGlobalFieldProps;
  alt_header?: boolean;
  _content_type_uid: 'copy_block';
}

export interface IOrContentBlockCSProps extends IDefaultCsProps {
  theme: {
    theme: keyof typeof EThemes;
  };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  left_content: IMlcopyBlockCSProps[];
  right_content: TRightContent[];
  _content_type_uid: 'infographic_block';
}
export interface ICmsTestimonialCard {
  headline: string;
  _metadata: TMetaData;
  author_image: IImageProps[] | IImageGlobalCSProps[];
  quote: string;
  cta: IAtLinkCsProps[];
}

export interface TOrTestimonialBlockCSProps extends IDefaultCsProps {
  autoplay: boolean;
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  testimonial_cards: ICmsTestimonialCard[];
  _content_type_uid: 'testimonial_block';
}

export interface IImageProps {
  alt: string;
  desktop_image_url: string;
  entry_title: string;
  mobile_image_url: string;
  tablet_image_url: string;
  _metadata?: TMetaData;
}

export interface IImageGlobalCSProps {
  desktop_image_url?: IFileCsProps | null;
  tablet_image_url?: IFileCsProps | null;
  mobile_image_url: IFileCsProps;
  mobile_aspect_ratio: string;
  tablet_aspect_ratio?: string | null;
  desktop_aspect_ratio?: string | null;
  decorative: boolean;
  quality: string;
  priority: boolean;
  mobile_position: EImagePosition;
  tablet_position?: EImagePosition | null;
  desktop_position?: EImagePosition | null;
  _metadata?: TMetaData;
}

export interface IOrContentMediaBlockCSProps extends IDefaultCsProps {
  theme: {
    theme: keyof typeof EThemes;
  };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  media_size: keyof typeof EOrContentMediaBlockSize;
  left_content: IImageProps | IImageGlobalCSProps;
  right_content: IMlcopyBlockCSProps[];
  _content_type_uid: 'content_media_block';
}

export type TSpacing = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type TSpacingProp = { spacing_value: TSpacing; divider: boolean };

export interface IOrFaqListCSProps extends IDefaultCsProps {
  theme: {
    theme: keyof typeof EThemes;
  };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  left_content: IMlcopyBlockCSProps[];
  right_content: IMlAccordionBlockCSProps[];
  _content_type_uid: 'faq_list';
}

export interface IMlAccordionBlockCSProps extends IDefaultCsProps {
  title: string;
  items: {
    accordion_item: {
      title: string;
      body: string;
    };
  }[];
  _content_type_uid: 'accordion';
}

export interface TOrFeaturedVideoCSProps extends IDefaultCsProps {
  description?: string;
  title?: string;
  video_button_aria_label?: string;
  video_url: string;
  video_thumbnail: IImageProps | IImageGlobalCSProps;
  video_title?: string;
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  _content_type_uid: 'featured_video';
}

export interface IOrVideoHeroCSProps extends IDefaultCsProps {
  title: string;
  hero_description: string;
  header_image: IImageGlobalCSProps;
  hero_video: IFileCsProps;
  _content_type_uid: 'video_hero';
}

export interface TOrPromotionalBlockCSProps extends IDefaultCsProps {
  title: string;
  theme: {
    theme: keyof typeof EThemes;
  };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
  autoplay: boolean;
  promo_block_cta: IAtLinkCsProps;
  promo_block_description: string;
  _content_type_uid: 'promotional_block';
  promo_block_copy: IMlcopyBlockCSProps[];
}

export interface TOrExteriorSwitcherCSProps extends IDefaultCsProps {
  title: string;
  autoplay: boolean;
  mobile_drawer_title: string;
  mobile_drawer_subtitle: string;
  carousel_content_description: string;
  _content_type_uid: 'exterior_switcher';
}

export interface TOrHomePlanHeroCSProps {
  autoplay?: {
    autoplay?: boolean;
  };
  slide_image?: IImageGlobalCSProps;
  cta_slide?: {
    slide_content: Array<{
      slide_image?: IImageGlobalCSProps;
      cta?: IAtLinkGlobalFieldProps;
      header?: {
        header?: string;
      };
    }>;
  };
  tooltip?: {
    body: string;
  };
}

export interface IOrFilteredGridProps extends IDefaultCsProps {
  filter_type: string;
  price_tooltip_text: string;
  _content_type_uid: 'filtered_grid';
}

export interface IOrMappingToolCSProps extends IDefaultCsProps {
  show_mapping_tool: boolean;
  _content_type_uid: 'mapping_tool';
}

export interface IMlCtaCardBlockCS extends IDefaultCsProps {
  theme: IGlobalThemeField;
  content_title?: string;
  description?: string;
  show_cta?: boolean;
  aria_label?: string;
  heading_element?: { select: THeaderSupportedElements };
  heading_variant?: { select: THeaderSupportedElements };
  headingAlt?: boolean;
  link: IAtLinkGlobalFieldProps;
  icon: { icon: EIcons | undefined };
  background_image?: IImageCMSProps;
  size: EClickableSize;
  use_overlay?: boolean;
  _content_type_uid?: 'cta_cards_1_3_4';
}

export interface IOtherContacts {
  department_name: string;
  department_phone_number: string;
}

export interface IMlServiceLocationsCS extends IDefaultCsProps {
  department_contacts?: IOtherContacts[];
  show_service_locations: boolean;
  _content_type_uid?: 'service_locations';
}

export interface ICustomUrls extends IDefaultCsProps {
  title: string;
  custom_url: string;
}

export interface ISeriesModelHomeGridCMSProps extends IDefaultCsProps {
  _content_type_uid: 'series_model_home_grid';
  plan_series_name: { select: string };
  grid_heading?: string;
  heading_element?: { select: THeaderSupportedElements };
  heading_variant?: { select: THeaderSupportedElements };
  top_spacing: TSpacingProp;
  bottom_spacing: TSpacingProp;
}

export interface IAtGalleryImagesAndCount {
  assets: IAtGalleryImageProps[];
  totalImgCount: number;
}
export interface IAtGalleryImageProps extends IDefaultCsProps {
  title: string;
  image: IFileCMSProps[];
  priority?: number;
  attributions: {
    plan_series: {
      select: string | null | undefined;
    };
    studio: {
      location: string | null | undefined;
    };
    room: {
      room_type: string | null | undefined;
    };
    exterior: {
      exterior_name: string | null | undefined;
    };
  };
  publish_details: {
    time: string;
  };
  _content_type_uid: 'at_gallery_image';
}

export interface IFileCMSProps extends IDefaultCsProps {
  filename: string;
  url: string;
  file_size: string;
  is_dir: boolean;
  content_type: string;
  parent_uid: string | null;
  tags?: string[];
  publish_details?: {
    time: string;
    user: string;
    locale: string;
  };
  dimension?: {
    width: number;
    height: number;
  };
  // description comes from asset file, however we cannot require it
  // used for alt text on image assets
  description?: string;
}
